import { type ReactNode, useEffect, useRef } from 'react';

type LegalModalProps = {
  open: boolean;
  title: string;
  lastUpdated: string;
  children: ReactNode;
  onClose: () => void;
};

export function LegalModal({ open, title, lastUpdated, children, onClose }: LegalModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('r42-modal-open');
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => dialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus(), 0);
    return () => {
      document.body.classList.remove('r42-modal-open');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="r42-legal-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="r42-legal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="r42-legal-modal-title"
      >
        <header className="r42-legal-modal-header">
          <div>
            <span className="r42-marketing-eyebrow">Route42 Legal</span>
            <h2 id="r42-legal-modal-title">{title}</h2>
            <p>Last Updated: {lastUpdated}</p>
          </div>
          <button className="r42-legal-modal-close" type="button" aria-label={`Close ${title}`} onClick={onClose}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>
        <div className="r42-legal-modal-body">{children}</div>
      </div>
    </div>
  );
}

export function TermsOfServiceContent() {
  return (
    <div className="r42-legal-copy">
      <section>
        <h3>1. Agreement to Terms</h3>
        <p>By accessing and using the Route42 website (&quot;Site&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, please do not use our Site.</p>
      </section>
      <section>
        <h3>2. Description of Service</h3>
        <p>Route42 provides a landing page that offers information about our industrial machine health tracking and GenAI co-pilot solutions for Predictive Maintenance (PdM). Through our Site, users can request demos and pilot programs of our services.</p>
      </section>
      <section>
        <h3>3. Use of the Site</h3>
        <p>You agree to use the Site only for lawful purposes and in accordance with these Terms. You agree not to:</p>
        <ul>
          <li>Use the Site in any way that violates applicable laws or regulations</li>
          <li>Attempt to gain unauthorized access to any portion of the Site</li>
          <li>Interfere with the proper working of the Site</li>
          <li>Engage in any data mining, scraping, or similar data gathering activities</li>
          <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
        </ul>
      </section>
      <section>
        <h3>4. Intellectual Property Rights</h3>
        <p>The Site and its original content, features, and functionality are owned by Route42 and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
      </section>
      <section>
        <h3>5. User Submissions</h3>
        <p>By submitting information through our forms:</p>
        <ul>
          <li>You grant us the right to process and store your information as described in our Privacy Policy</li>
          <li>You represent that you have the legal right to submit such information</li>
          <li>You acknowledge that any confidential or proprietary information shared is provided at your own risk</li>
        </ul>
      </section>
      <section>
        <h3>6. Disclaimer of Warranties</h3>
        <p className="r42-legal-emphasis">THE SITE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. ROUTE42 DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
      </section>
      <section>
        <h3>7. Limitation of Liability</h3>
        <p className="r42-legal-emphasis">IN NO EVENT SHALL ROUTE42, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, PUNITIVE, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR IN ANY WAY CONNECTED WITH:</p>
        <ul className="r42-legal-emphasis">
          <li>THE USE OR INABILITY TO USE THE SITE</li>
          <li>ANY INFORMATION PROVIDED BY THE SITE</li>
          <li>ANY DELAY OR INABILITY TO USE THE SITE</li>
          <li>WHETHER BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE.</li>
        </ul>
      </section>
      <section>
        <h3>8. Demo and Pilot Requests</h3>
        <ul>
          <li>Submission of a demo or pilot request does not guarantee acceptance</li>
          <li>Route42 reserves the right to decline any request at its sole discretion</li>
          <li>Any terms related to demos or pilot programs will be governed by separate agreements</li>
        </ul>
      </section>
      <section>
        <h3>9. Links to Third-Party Sites</h3>
        <p>The Site may contain links to third-party websites. Route42 is not responsible for the content or privacy practices of any linked sites and encourages you to review the terms and privacy policies of any third-party sites you visit.</p>
      </section>
      <section>
        <h3>10. Modifications</h3>
        <p>We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days&apos; notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.</p>
      </section>
      <section>
        <h3>11. Governing Law</h3>
        <p>These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.</p>
      </section>
      <section>
        <h3>12. Dispute Resolution</h3>
        <p>Any dispute arising from these Terms shall be resolved through:</p>
        <ol>
          <li>Informal negotiation initiated through written notice</li>
          <li>If unsuccessful, mandatory mediation in [City], California</li>
          <li>If mediation fails, binding arbitration conducted in accordance with the rules of the American Arbitration Association</li>
        </ol>
      </section>
      <section>
        <h3>13. Severability</h3>
        <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that the Terms shall otherwise remain in full force and effect and enforceable.</p>
      </section>
      <section>
        <h3>14. Assignment</h3>
        <p>Route42 may assign or transfer these Terms, in whole or in part, without restriction. You may not assign or transfer your rights under these Terms without prior written consent from Route42.</p>
      </section>
      <section>
        <h3>15. Contact Information</h3>
        <p>If you have any questions about these Terms, please contact us at:</p>
        <address>
          Route42<br />
          [Insert Company Address]<br />
          Email: [Insert Legal Contact Email]<br />
          Phone: [Insert Contact Phone Number]
        </address>
      </section>
      <section>
        <h3>16. Entire Agreement</h3>
        <p>These Terms constitute the entire agreement between you and Route42 regarding your use of the Site and supersede all prior and contemporaneous written or oral agreements between us.</p>
      </section>
    </div>
  );
}

export function PrivacyPolicyContent() {
  return (
    <div className="r42-legal-copy">
      <section>
        <h3>Introduction</h3>
        <p>Route42 (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website or interact with our landing page.</p>
      </section>
      <section>
        <h3>Information We Collect</h3>
        <p>We collect information that you voluntarily provide to us through forms on our website when you:</p>
        <ul>
          <li>Request a demo of our industrial machine health tracking and GenAI co-pilot solutions</li>
          <li>Request a pilot program for our predictive maintenance solutions</li>
        </ul>
        <p>The information we collect may include:</p>
        <ul>
          <li>Your name</li>
          <li>Business email address</li>
          <li>Company name</li>
          <li>Phone number</li>
          <li>Job title</li>
          <li>Any additional information you choose to provide in message fields</li>
        </ul>
      </section>
      <section>
        <h3>How We Use Your Information</h3>
        <p>We use the information you provide to:</p>
        <ul>
          <li>Respond to your requests for demos or pilot programs</li>
          <li>Communicate with you about our products and services</li>
          <li>Process and evaluate pilot program requests</li>
          <li>Improve our website and services</li>
          <li>Comply with legal obligations</li>
        </ul>
      </section>
      <section>
        <h3>Data Storage and Security</h3>
        <p>We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or method of electronic storage is 100% secure.</p>
      </section>
      <section>
        <h3>Third-Party Services</h3>
        <p>We do not share your personal information with third parties except as necessary to provide our services or as required by law. Any third-party service providers we use are bound by contractual obligations to keep personal information confidential and use it only for the purposes for which we disclose it to them.</p>
      </section>
      <section>
        <h3>Cookies and Tracking</h3>
        <p>Our landing page is static and does not use cookies or tracking technologies beyond what is strictly necessary for the website to function.</p>
      </section>
      <section>
        <h3>Your Rights</h3>
        <p>You have the right to:</p>
        <ul>
          <li>Access your personal information</li>
          <li>Correct inaccurate personal information</li>
          <li>Request deletion of your personal information</li>
          <li>Object to our processing of your personal information</li>
          <li>Request restriction of processing your personal information</li>
          <li>Request transfer of your personal information</li>
        </ul>
      </section>
      <section>
        <h3>International Data Transfers</h3>
        <p>If you are located outside the United States, your information may be transferred to and processed in the United States. By providing your information, you consent to such transfer and processing.</p>
      </section>
      <section>
        <h3>Children&apos;s Privacy</h3>
        <p>Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.</p>
      </section>
      <section>
        <h3>Changes to This Privacy Policy</h3>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the &quot;Last Updated&quot; date.</p>
      </section>
      <section>
        <h3>Contact Us</h3>
        <p>If you have any questions about this Privacy Policy or our practices, please contact us at:</p>
        <address>
          Route42<br />
          [Insert Company Address]<br />
          Email: [Insert Privacy Contact Email]
        </address>
      </section>
      <section>
        <h3>Governing Law</h3>
        <p>This Privacy Policy is governed by the laws of the State of California, United States.</p>
      </section>
    </div>
  );
}
